import React from 'react'
import { Activity, Users, CheckCircle, AlertCircle, BarChart3 } from 'lucide-react'
import { 
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer 
} from 'recharts'

const COLORS = ['#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e']

export default function AnalyticsTab({ analyticsData, analyticsLoading, analyticsDays, setAnalyticsDays }) {
  return (
    <div className="space-y-6">
      {/* Time Range Selector */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">API Usage Analytics</h2>
        <select
          value={analyticsDays}
          onChange={(e) => setAnalyticsDays(Number(e.target.value))}
          className="px-3 py-2 border border-zinc-600 rounded-lg text-sm"
        >
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
        </select>
      </div>

      {analyticsLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
        </div>
      ) : analyticsData ? (
        <>
          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard 
              icon={Activity}
              iconBg="bg-green-500/100/20"
              iconColor="text-green-500"
              value={analyticsData.total_calls?.toLocaleString() || 0}
              label="Total API Calls"
            />
            <StatCard 
              icon={Users}
              iconBg="bg-green-500/20"
              iconColor="text-green-600"
              value={analyticsData.total_active_partners || 0}
              label="Active Partners"
            />
            <StatCard 
              icon={CheckCircle}
              iconBg="bg-blue-500/20"
              iconColor="text-blue-400"
              value={analyticsData.webhook_stats?.delivered || 0}
              label="Webhooks Delivered"
            />
            <StatCard 
              icon={AlertCircle}
              iconBg="bg-red-500/20"
              iconColor="text-red-400"
              value={analyticsData.webhook_stats?.failed || 0}
              label="Webhooks Failed"
            />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Daily API Calls Chart */}
            <div className="bg-zinc-800 rounded-xl border border-zinc-700 p-6">
              <h3 className="font-semibold mb-4">Daily API Calls</h3>
              {analyticsData.daily_calls?.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={analyticsData.daily_calls}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      tickFormatter={(val) => val.slice(5)}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Line 
                      type="monotone" 
                      dataKey="count" 
                      stroke="#6366f1" 
                      strokeWidth={2}
                      dot={{ fill: '#6366f1' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[250px] flex items-center justify-center text-zinc-500">
                  No data available
                </div>
              )}
            </div>

            {/* Hourly Distribution Chart */}
            <div className="bg-zinc-800 rounded-xl border border-zinc-700 p-6">
              <h3 className="font-semibold mb-4">Today's Hourly Distribution</h3>
              {analyticsData.hourly_calls_today?.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={analyticsData.hourly_calls_today}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
                    <XAxis 
                      dataKey="hour" 
                      tick={{ fontSize: 12 }}
                      tickFormatter={(val) => `${val}:00`}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip labelFormatter={(val) => `${val}:00`} />
                    <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[250px] flex items-center justify-center text-zinc-500">
                  No API calls today
                </div>
              )}
            </div>
          </div>

          {/* Bottom Row: Top Partners and Endpoints */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Partners */}
            <div className="bg-zinc-800 rounded-xl border border-zinc-700 p-6">
              <h3 className="font-semibold mb-4">Top Partners by API Calls</h3>
              {analyticsData.calls_by_partner?.length > 0 ? (
                <div className="space-y-3">
                  {analyticsData.calls_by_partner.map((partner, idx) => (
                    <div key={partner.partner_id} className="flex items-center gap-3">
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold"
                        style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                      >
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{partner.partner_name}</p>
                        <div className="w-full bg-zinc-800 rounded-full h-2 mt-1">
                          <div 
                            className="h-2 rounded-full"
                            style={{ 
                              width: `${(partner.count / analyticsData.calls_by_partner[0].count) * 100}%`,
                              backgroundColor: COLORS[idx % COLORS.length]
                            }}
                          />
                        </div>
                      </div>
                      <span className="text-sm font-medium text-zinc-300">
                        {partner.count.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-zinc-500">
                  No partner activity
                </div>
              )}
            </div>

            {/* Top Endpoints */}
            <div className="bg-zinc-800 rounded-xl border border-zinc-700 p-6">
              <h3 className="font-semibold mb-4">Top Endpoints</h3>
              {analyticsData.top_endpoints?.length > 0 ? (
                <div className="space-y-2">
                  {analyticsData.top_endpoints.slice(0, 8).map((endpoint, idx) => (
                    <div key={idx} className="flex items-center gap-3 py-2 border-b border-zinc-700 last:border-0">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${
                        endpoint.method === 'GET' ? 'bg-green-500/20 text-green-400' :
                        endpoint.method === 'POST' ? 'bg-blue-500/20 text-blue-400' :
                        endpoint.method === 'PUT' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {endpoint.method}
                      </span>
                      <code className="text-xs font-mono text-zinc-300 flex-1 truncate">
                        {endpoint.endpoint}
                      </code>
                      <span className="text-sm font-medium text-zinc-300">
                        {endpoint.count.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-zinc-500">
                  No endpoint activity
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="bg-zinc-800 rounded-xl border border-zinc-700 p-12 text-center">
          <BarChart3 className="w-12 h-12 mx-auto mb-3 text-zinc-500" />
          <p className="text-zinc-400">No analytics data available</p>
          <p className="text-sm text-zinc-500 mt-1">Data will appear once partners start making API calls</p>
        </div>
      )}
    </div>
  )
}

function StatCard({ icon: Icon, iconBg, iconColor, value, label }) {
  return (
    <div className="bg-zinc-800 rounded-xl p-4 border border-zinc-700">
      <div className="flex items-center gap-3">
        <div className={`p-2 ${iconBg} rounded-lg`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm text-zinc-400">{label}</p>
        </div>
      </div>
    </div>
  )
}
